import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Trophy, X, Share2, Crown, ImageIcon, Loader2, Gift, Camera } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WINNING_SCORE = 100;

// Winner Popup Component
const WinnerPopup = ({ score, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4">
      <div className="bg-gradient-to-br from-yellow-400 via-orange-500 to-pink-500 rounded-3xl max-w-md w-full p-1 animate-pulse">
        <div className="bg-white rounded-3xl p-6 text-center">
          <div className="text-6xl mb-4">🏆🎉</div>
          <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-pink-500 mb-2">
            YOU WON!
          </h2>
          <p className="text-5xl font-bold text-gray-800 mb-4">{score} Points!</p>
          
          <div className="bg-gradient-to-r from-green-400 to-emerald-500 rounded-2xl p-4 mb-4 text-white">
            <Gift className="w-10 h-10 mx-auto mb-2" />
            <p className="text-lg font-bold">You just won a DATE!</p>
            <p className="text-3xl font-bold my-2">$100 USD</p>
            <p className="text-sm opacity-90">Courtesy of</p>
            <p className="text-xl font-bold">Matrix Shopping App</p>
          </div>

          <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-4 mb-4">
            <Camera className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
            <p className="text-yellow-800 font-bold text-lg">📸 SCREENSHOT THIS!</p>
            <p className="text-yellow-700 text-sm">Save this as proof of your win!</p>
          </div>

          <a
            href="https://wa.me/5926665489?text=I%20have%20won%20a%20date%20from%20the%20Matrix%20Shopping%20App"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full bg-gradient-to-r from-pink-500 to-purple-600 text-white py-3 rounded-full font-bold text-lg text-center hover:opacity-90 transition-opacity"
          >
            Claim My Prize! 🎁
          </a>
        </div>
      </div>
    </div>
  );
};

// Photo Upload Screen Component
const PhotoUploadScreen = ({ onStart, challengerPhotos, opponentPhotos, challengerName, hasBothPhotos }) => {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (photos.length + files.length > 10) {
      toast.error('Maximum 10 photos allowed');
      return;
    }

    setUploading(true);
    const newPhotos = [];

    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) continue;
        if (file.size > 5 * 1024 * 1024) {
          toast.error('Each image must be less than 5MB');
          continue;
        }

        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API}/upload/photo`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true
        });

        newPhotos.push(response.data.url);
      }

      setPhotos(prev => [...prev, ...newPhotos]);
      if (newPhotos.length > 0) {
        toast.success(`${newPhotos.length} photo(s) added!`);
      }
    } catch (error) {
      toast.error('Failed to upload photos');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-4">
          <div className="text-5xl mb-3">🏓❤️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {hasBothPhotos ? 'Continue the Challenge!' : challengerPhotos?.length > 0 ? 'Your Turn!' : 'Ping Pong Challenge'}
          </h2>
          <p className="text-gray-600 text-sm">
            {hasBothPhotos 
              ? 'Both players have photos! They will alternate during the game.'
              : `Upload your photos for ${challengerName || 'your opponent'} to see!`}
          </p>
        </div>

        {/* Show both players' photos if we have them */}
        {hasBothPhotos && (
          <div className="mb-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-3">
            <p className="text-sm font-medium text-gray-700 mb-2 text-center">
              📸 Photos will alternate each point!
            </p>
            <div className="flex gap-4 justify-center">
              <div className="text-center">
                <p className="text-xs text-purple-600 mb-1">Your photos</p>
                <div className="flex gap-1">
                  {opponentPhotos?.slice(0, 3).map((url, idx) => (
                    <img key={idx} src={url} alt="" className="w-10 h-10 rounded object-cover border-2 border-purple-300" />
                  ))}
                </div>
              </div>
              <div className="text-center">
                <p className="text-xs text-pink-600 mb-1">{challengerName}'s</p>
                <div className="flex gap-1">
                  {challengerPhotos?.slice(0, 3).map((url, idx) => (
                    <img key={idx} src={url} alt="" className="w-10 h-10 rounded object-cover border-2 border-pink-300" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Show challenger's photos preview if first time responding */}
        {!hasBothPhotos && challengerPhotos?.length > 0 && (
          <div className="mb-4 bg-purple-50 rounded-xl p-3">
            <p className="text-sm text-purple-700 font-medium mb-2">
              📸 {challengerName}'s photos you'll see:
            </p>
            <div className="flex gap-1 overflow-x-auto pb-1">
              {challengerPhotos.slice(0, 6).map((url, idx) => (
                <img key={idx} src={url} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
              ))}
              {challengerPhotos.length > 6 && (
                <div className="w-12 h-12 rounded-lg bg-purple-200 flex items-center justify-center flex-shrink-0">
                  <span className="text-purple-700 text-xs font-bold">+{challengerPhotos.length - 6}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Upload Area - only if not hasBothPhotos */}
        {!hasBothPhotos && (
          <>
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Upload YOUR photos:
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoSelect}
                className="hidden"
                id="game-photo-upload"
              />
              <label
                htmlFor="game-photo-upload"
                className="border-2 border-dashed border-pink-300 rounded-xl p-4 flex flex-col items-center cursor-pointer hover:border-pink-500 hover:bg-pink-50 transition-colors"
              >
                {uploading ? (
                  <Loader2 className="w-8 h-8 text-pink-500 animate-spin" />
                ) : (
                  <>
                    <ImageIcon className="w-8 h-8 text-pink-400 mb-1" />
                    <p className="text-pink-600 font-medium text-sm">Click to add your photos</p>
                    <p className="text-gray-400 text-xs">Up to 10 photos</p>
                  </>
                )}
              </label>
            </div>

            {/* Photo Preview Grid */}
            {photos.length > 0 && (
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">Your photos ({photos.length}):</p>
                <div className="grid grid-cols-5 gap-1">
                  {photos.map((url, idx) => (
                    <div key={idx} className="relative aspect-square rounded-lg overflow-hidden">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => removePhoto(idx)}
                        className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Prize Info */}
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-3 mb-4">
          <div className="flex items-center justify-center space-x-2 mb-1">
            <Trophy className="w-5 h-5 text-yellow-500" />
            <span className="font-bold text-yellow-700">Win a $100 Date!</span>
          </div>
          <p className="text-xs text-yellow-600 text-center">
            First to reach {WINNING_SCORE} points wins! Courtesy of Matrix Shopping App
          </p>
        </div>

        {/* Buttons */}
        <div className="space-y-2">
          <Button
            onClick={() => onStart(photos)}
            disabled={!hasBothPhotos && photos.length === 0}
            className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white py-3 rounded-full font-semibold disabled:opacity-50"
          >
            {hasBothPhotos 
              ? '🎮 Continue Playing!'
              : photos.length > 0 
                ? `Start Game with ${photos.length} Photos 🎮` 
                : 'Upload photos to continue'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Game Component
const PingPongGame = ({ 
  onClose, 
  onShareScore, 
  playerName, 
  opponentName, 
  matchId, 
  userHighScore, 
  opponentHighScore,
  challengerPhotos = [], // Photos from the person who sent the challenge (userA)
  opponentPhotos = [] // Photos from the previous player (userB) - for continuing games
}) => {
  const [showPhotoUpload, setShowPhotoUpload] = useState(true);
  const [userAPhotos, setUserAPhotos] = useState(challengerPhotos); // Original challenger's photos
  const [userBPhotos, setUserBPhotos] = useState(opponentPhotos); // Responder's photos
  const [myNewPhotos, setMyNewPhotos] = useState([]); // New photos I just uploaded
  const [gameStarted, setGameStarted] = useState(false);

  // Check if we already have photos from both users
  const hasBothPhotos = challengerPhotos.length > 0 && opponentPhotos.length > 0;

  const handleStartGame = (uploadedPhotos) => {
    if (hasBothPhotos) {
      // Continue with existing photos
      setMyNewPhotos([]);
    } else {
      // First time - save uploaded photos
      setMyNewPhotos(uploadedPhotos);
      if (challengerPhotos.length === 0) {
        // I'm the first player (User A)
        setUserAPhotos(uploadedPhotos);
      } else {
        // I'm responding (User B)
        setUserBPhotos(uploadedPhotos);
      }
    }
    setShowPhotoUpload(false);
    setGameStarted(true);
  };

  if (showPhotoUpload) {
    return (
      <PhotoUploadScreen 
        onStart={handleStartGame}
        challengerPhotos={challengerPhotos}
        opponentPhotos={opponentPhotos}
        challengerName={opponentName}
        hasBothPhotos={hasBothPhotos}
      />
    );
  }

  if (!gameStarted) return null;

  // Determine which photos to use for display
  let displayPhotos = [];
  if (hasBothPhotos) {
    // Interleave both sets of photos: A1, B1, A2, B2, A3, B3...
    const maxLen = Math.max(userAPhotos.length, userBPhotos.length);
    for (let i = 0; i < maxLen; i++) {
      if (i < userAPhotos.length) displayPhotos.push({ url: userAPhotos[i], owner: 'A' });
      if (i < userBPhotos.length) displayPhotos.push({ url: userBPhotos[i], owner: 'B' });
    }
  } else if (challengerPhotos.length > 0) {
    // Responding to challenge - show challenger's photos
    displayPhotos = challengerPhotos.map(url => ({ url, owner: 'challenger' }));
  } else if (myNewPhotos.length > 0) {
    // First player - show their own photos they just uploaded
    displayPhotos = myNewPhotos.map(url => ({ url, owner: 'A' }));
  }

  // Determine what photos to share
  let photosToShare = {
    userAPhotos: hasBothPhotos ? userAPhotos : (challengerPhotos.length === 0 ? myNewPhotos : challengerPhotos),
    userBPhotos: hasBothPhotos ? userBPhotos : (challengerPhotos.length > 0 ? myNewPhotos : [])
  };

  return (
    <GameCanvas 
      onClose={onClose}
      onShareScore={onShareScore}
      playerName={playerName}
      opponentName={opponentName}
      matchId={matchId}
      userHighScore={userHighScore}
      opponentHighScore={opponentHighScore}
      displayPhotos={displayPhotos}
      photosToShare={photosToShare}
      hasBothPhotos={hasBothPhotos || (challengerPhotos.length > 0 && myNewPhotos.length > 0)}
    />
  );
};

// Game Canvas Component
const GameCanvas = ({ 
  onClose, 
  onShareScore, 
  playerName, 
  opponentName, 
  matchId, 
  userHighScore, 
  opponentHighScore, 
  displayPhotos,
  photosToShare,
  hasBothPhotos
}) => {
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [highScore, setHighScore] = useState(userHighScore || 0);
  const [opponentBestScore, setOpponentBestScore] = useState(opponentHighScore || 0);
  const [isNewHighScore, setIsNewHighScore] = useState(false);
  const [isWinner, setIsWinner] = useState(false);
  const [showWinnerPopup, setShowWinnerPopup] = useState(false);
  const gameRef = useRef(null);
  const loadedImagesRef = useRef([]);
  const currentScoreRef = useRef(0);

  // Save high score function - declared before useEffect that uses it
  const saveHighScore = async (newScore) => {
    try {
      await axios.post(`${API}/pingpong/score`, {
        match_id: matchId,
        score: newScore
      }, { withCredentials: true });
    } catch (e) {
      // Silently fail
    }
  };

  // Preload images
  useEffect(() => {
    if (displayPhotos.length > 0) {
      displayPhotos.forEach((photo, idx) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = photo.url;
        img.onload = () => {
          loadedImagesRef.current[idx] = { img, owner: photo.owner };
        };
      });
    }
  }, [displayPhotos]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width = 400;
    const height = canvas.height = 600;

    let audioContext;
    let gainNode;
    let beatInterval;
    
    const createMellowBeat = () => {
      try {
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        gainNode = audioContext.createGain();
        gainNode.connect(audioContext.destination);
        gainNode.gain.value = 0.15;

        const playBeat = () => {
          const kickOsc = audioContext.createOscillator();
          const kickGain = audioContext.createGain();
          
          kickOsc.connect(kickGain);
          kickGain.connect(gainNode);
          
          kickOsc.frequency.setValueAtTime(150, audioContext.currentTime);
          kickOsc.frequency.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
          
          kickGain.gain.setValueAtTime(0.3, audioContext.currentTime);
          kickGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
          
          kickOsc.start(audioContext.currentTime);
          kickOsc.stop(audioContext.currentTime + 0.3);

          setTimeout(() => {
            try {
              const hihatOsc = audioContext.createOscillator();
              const hihatGain = audioContext.createGain();
              
              hihatOsc.type = 'square';
              hihatOsc.connect(hihatGain);
              hihatGain.connect(gainNode);
              
              hihatOsc.frequency.setValueAtTime(800, audioContext.currentTime);
              hihatGain.gain.setValueAtTime(0.05, audioContext.currentTime);
              hihatGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.05);
              
              hihatOsc.start(audioContext.currentTime);
              hihatOsc.stop(audioContext.currentTime + 0.05);
            } catch (e) {}
          }, 300);
        };

        playBeat();
        beatInterval = setInterval(playBeat, 600);
      } catch (error) {}
    };

    createMellowBeat();

    const paddle = {
      x: width / 2 - 50,
      y: height - 30,
      width: 100,
      height: 10,
      speed: 8
    };

    // Random starting direction - FAST BALL
    const randomAngle = (Math.random() - 0.5) * 2; // -1 to 1
    const ball = {
      x: width / 2 + (Math.random() - 0.5) * 100, // Random starting X position
      y: 50,       // Start near top so ball moves toward paddle
      radius: 14,  // Larger ball for better visibility
      dx: 8 * (randomAngle > 0 ? 1 : -1) + randomAngle * 3, // FAST horizontal direction
      dy: 10,      // FAST - Moving DOWN toward paddle (positive = down)
      speed: 10,   // FAST base speed
      maxSpeed: 18 // Higher max speed
    };

    let animationId;
    let keys = {};

    const handleKeyDown = (e) => { keys[e.key] = true; };
    const handleKeyUp = (e) => { keys[e.key] = false; };

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      paddle.x = Math.max(0, Math.min(width - paddle.width, mouseX - paddle.width / 2));
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0];
      const touchX = touch.clientX - rect.left;
      paddle.x = Math.max(0, Math.min(width - paddle.width, touchX - paddle.width / 2));
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });

    const drawPaddle = () => {
      const gradient = ctx.createLinearGradient(paddle.x, paddle.y, paddle.x, paddle.y + paddle.height);
      gradient.addColorStop(0, '#A78BFA');
      gradient.addColorStop(1, '#8B5CF6');
      ctx.fillStyle = gradient;
      ctx.fillRect(paddle.x, paddle.y, paddle.width, paddle.height);
    };

    const drawBall = () => {
      // Draw heart emoji as the ball - bigger and more visible
      ctx.font = `${ball.radius * 3}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('❤️', ball.x, ball.y);
    };

    const drawScore = () => {
      ctx.fillStyle = '#6B7280';
      ctx.font = 'bold 24px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(`Score: ${currentScoreRef.current}`, width / 2, 40);
      
      ctx.fillStyle = '#EC4899';
      ctx.font = 'bold 14px Arial';
      ctx.fillText(`Goal: ${WINNING_SCORE} 🏆`, width / 2, 65);
    };

    const drawPhoto = () => {
      if (displayPhotos.length === 0) return;
      if (currentScoreRef.current === 0) return;

      // Show photos 1 by 1: User A photo 1, User B photo 1, User A photo 2, etc.
      const photoIdx = (currentScoreRef.current - 1) % displayPhotos.length;
      
      if (!loadedImagesRef.current[photoIdx]) return;

      const { img, owner } = loadedImagesRef.current[photoIdx];
      const maxSize = 160;
      const scale = Math.min(maxSize / img.width, maxSize / img.height);
      const imgWidth = img.width * scale;
      const imgHeight = img.height * scale;
      const imgX = (width - imgWidth) / 2;
      const imgY = (height - imgHeight) / 2 - 50;

      // Different border color based on owner - Purple for Player 1, Pink for Player 2
      const isPlayer1 = owner === 'A' || owner === 'challenger';
      const borderColor = isPlayer1 ? '#8B5CF6' : '#EC4899';
      const bgColor = isPlayer1 ? 'rgba(139, 92, 246, 0.1)' : 'rgba(236, 72, 153, 0.1)';

      // Background with owner color tint
      ctx.fillStyle = bgColor;
      ctx.beginPath();
      ctx.roundRect(imgX - 12, imgY - 35, imgWidth + 24, imgHeight + 55, 16);
      ctx.fill();

      // White background for image
      ctx.fillStyle = 'rgba(255, 255, 255, 0.98)';
      ctx.beginPath();
      ctx.roundRect(imgX - 6, imgY - 6, imgWidth + 12, imgHeight + 12, 10);
      ctx.fill();

      // Border
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = 4;
      ctx.stroke();

      // Draw the image
      ctx.save();
      ctx.beginPath();
      ctx.roundRect(imgX, imgY, imgWidth, imgHeight, 8);
      ctx.clip();
      ctx.drawImage(img, imgX, imgY, imgWidth, imgHeight);
      ctx.restore();

      // Label showing whose photo - with background
      const ownerLabel = isPlayer1 ? '💜 Player 1' : '💗 Player 2';
      const photoNum = Math.floor((currentScoreRef.current - 1) / 2) + 1;
      
      ctx.fillStyle = borderColor;
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(`${ownerLabel} - Photo ${photoNum}`, width / 2, imgY - 15);
    };

    const update = () => {
      if (keys['ArrowLeft'] && paddle.x > 0) {
        paddle.x -= paddle.speed;
      }
      if (keys['ArrowRight'] && paddle.x < width - paddle.width) {
        paddle.x += paddle.speed;
      }

      ball.x += ball.dx;
      ball.y += ball.dy;

      // Wall collision with slight angle variation
      if (ball.x + ball.radius > width) {
        ball.x = width - ball.radius;
        ball.dx = -Math.abs(ball.dx);
        ball.dy += (Math.random() - 0.5) * 2; // Add slight vertical variation
      }
      if (ball.x - ball.radius < 0) {
        ball.x = ball.radius;
        ball.dx = Math.abs(ball.dx);
        ball.dy += (Math.random() - 0.5) * 2; // Add slight vertical variation
      }

      // Top wall collision with angle variation
      if (ball.y - ball.radius < 0) {
        ball.y = ball.radius;
        ball.dy = Math.abs(ball.dy);
        ball.dx += (Math.random() - 0.5) * 3; // Add horizontal variation on top bounce
        // Cap horizontal speed
        ball.dx = Math.max(-10, Math.min(10, ball.dx));
      }

      // Paddle collision - check if ball hits paddle
      if (
        ball.y + ball.radius >= paddle.y &&
        ball.y - ball.radius <= paddle.y + paddle.height &&
        ball.x + ball.radius > paddle.x &&
        ball.x - ball.radius < paddle.x + paddle.width &&
        ball.dy > 0
      ) {
        // Calculate where on the paddle the ball hit (0 = left edge, 1 = right edge)
        const hitPosition = (ball.x - paddle.x) / paddle.width;
        
        // Convert to angle multiplier (-1 = far left, 0 = center, 1 = far right)
        const angleMultiplier = (hitPosition - 0.5) * 2;
        
        // Bounce the ball back up
        ball.y = paddle.y - ball.radius; // Reset position above paddle
        ball.dy = -Math.abs(ball.dy);    // Ensure going up
        
        // Add angle based on where ball hit paddle + some randomness
        const baseSpeed = Math.sqrt(ball.dx * ball.dx + ball.dy * ball.dy);
        const randomFactor = (Math.random() - 0.5) * 2; // -1 to 1
        ball.dx = angleMultiplier * 8 + randomFactor * 2;
        
        // Ensure minimum horizontal movement
        if (Math.abs(ball.dx) < 2) {
          ball.dx = (Math.random() > 0.5 ? 1 : -1) * (2 + Math.random() * 3);
        }
        
        // Cap the horizontal speed
        ball.dx = Math.max(-10, Math.min(10, ball.dx));
        
        currentScoreRef.current++;
        setScore(currentScoreRef.current);
        
        if (currentScoreRef.current >= WINNING_SCORE) {
          cancelAnimationFrame(animationId);
          if (beatInterval) clearInterval(beatInterval);
          if (audioContext) audioContext.close().catch(() => {});
          
          setIsWinner(true);
          setShowWinnerPopup(true);
          setGameOver(true);
          saveHighScore(currentScoreRef.current);
          return;
        }
        
        // Gradually increase speed but cap it
        const speedIncrease = 1.02;
        ball.dy *= speedIncrease;
        if (Math.abs(ball.dy) > ball.maxSpeed) {
          ball.dy = ball.dy > 0 ? ball.maxSpeed : -ball.maxSpeed;
        }
        
        try {
          const hitOsc = audioContext.createOscillator();
          const hitGain = audioContext.createGain();
          
          hitOsc.connect(hitGain);
          hitGain.connect(gainNode);
          
          hitOsc.frequency.setValueAtTime(400, audioContext.currentTime);
          hitOsc.frequency.exponentialRampToValueAtTime(200, audioContext.currentTime + 0.1);
          
          hitGain.gain.setValueAtTime(0.2, audioContext.currentTime);
          hitGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
          
          hitOsc.start(audioContext.currentTime);
          hitOsc.stop(audioContext.currentTime + 0.1);
        } catch (e) {}
        
        canvas.style.transform = 'scale(1.02)';
        setTimeout(() => { canvas.style.transform = 'scale(1)'; }, 100);
      }

      if (ball.y + ball.radius > height) {
        cancelAnimationFrame(animationId);
        if (beatInterval) clearInterval(beatInterval);
        if (audioContext) audioContext.close().catch(() => {});
        
        const finalScore = currentScoreRef.current;
        const newHigh = finalScore > (userHighScore || 0);
        setIsNewHighScore(newHigh);
        
        if (newHigh) {
          setHighScore(finalScore);
          saveHighScore(finalScore);
        }
        
        setGameOver(true);
        return;
      }
    };

    const draw = () => {
      ctx.fillStyle = '#F3F4F6';
      ctx.fillRect(0, 0, width, height);

      ctx.strokeStyle = '#E5E7EB';
      ctx.lineWidth = 4;
      ctx.strokeRect(2, 2, width - 4, height - 4);

      drawPhoto();
      drawPaddle();
      drawBall();
      drawScore();
    };

    const gameLoop = () => {
      update();
      draw();
      animationId = requestAnimationFrame(gameLoop);
    };

    gameRef.current = { animationId, beatInterval, audioContext };
    gameLoop();

    return () => {
      cancelAnimationFrame(animationId);
      if (beatInterval) clearInterval(beatInterval);
      if (audioContext) audioContext.close().catch(() => {});
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('touchmove', handleTouchMove);
    };
  }, [displayPhotos, saveHighScore, userHighScore, opponentName]);

  const handleShare = () => {
    if (onShareScore) {
      // Share both sets of photos so they can continue
      onShareScore(score, opponentName, highScore, opponentBestScore, photosToShare);
    }
    toast.success('Score shared! Now wait for their turn! 🏓');
  };

  const handlePlayAgain = () => {
    currentScoreRef.current = 0;
    setScore(0);
    setGameOver(false);
    setIsNewHighScore(false);
    setIsWinner(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      {showWinnerPopup && (
        <WinnerPopup score={score} onClose={() => setShowWinnerPopup(false)} />
      )}
      
      <div className="bg-white rounded-2xl overflow-hidden shadow-2xl max-w-md w-full">
        <div className="bg-gradient-to-r from-pink-500 to-purple-600 p-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-2xl">🏓</span>
              <div>
                <h3 className="font-bold">Ping Pong Challenge</h3>
                <p className="text-xs opacity-90">First to {WINNING_SCORE} wins $100! 🏆</p>
              </div>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex justify-around py-3 bg-gray-50 border-b">
          <div className="text-center">
            <p className="text-xs text-gray-500">Your Best</p>
            <div className="flex items-center justify-center space-x-1">
              <Crown className="w-4 h-4 text-yellow-500" />
              <span className="font-bold text-purple-600">{highScore}</span>
            </div>
          </div>
          <div className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold">VS</div>
          <div className="text-center">
            <p className="text-xs text-gray-500">{opponentName}'s Best</p>
            <div className="flex items-center justify-center space-x-1">
              <Crown className="w-4 h-4 text-yellow-500" />
              <span className="font-bold text-pink-600">{opponentBestScore}</span>
            </div>
          </div>
        </div>

        <div className="p-4">
          {!gameOver ? (
            <div className="flex flex-col items-center">
              <canvas
                ref={canvasRef}
                className="border-2 border-purple-200 rounded-lg shadow-inner"
                style={{ maxWidth: '100%', touchAction: 'none' }}
              />
              <p className="text-xs text-gray-500 mt-2">
                Use ← → arrows or mouse/touch to move
              </p>
              {hasBothPhotos && (
                <p className="text-xs text-purple-500 mt-1">
                  📸 Photos alternate: Point 1 = Player 1, Point 2 = Player 2...
                </p>
              )}
            </div>
          ) : (
            <div className="text-center py-6">
              <div className="text-6xl mb-4">{isWinner ? '🏆' : isNewHighScore ? '⭐' : '🎮'}</div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                {isWinner ? 'WINNER!' : isNewHighScore ? 'New High Score!' : 'Game Over!'}
              </h3>
              <p className="text-4xl font-bold text-purple-600 mb-4">{score}</p>
              
              {isWinner && (
                <div className="bg-gradient-to-r from-yellow-100 to-orange-100 border-2 border-yellow-300 rounded-lg p-3 mb-4">
                  <p className="text-yellow-800 font-bold">
                    🎉 You reached {WINNING_SCORE}! You won a $100 Date!
                  </p>
                  <p className="text-yellow-700 text-sm">Screenshot your prize!</p>
                </div>
              )}

              {isNewHighScore && !isWinner && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <p className="text-yellow-800 font-semibold">
                    🎉 You beat your previous best of {userHighScore || 0}!
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Button
                  onClick={handlePlayAgain}
                  className="w-full bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-full"
                >
                  Play Again
                </Button>
                <Button
                  onClick={handleShare}
                  variant="outline"
                  className="w-full border-purple-300 text-purple-600 rounded-full"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share & Challenge Back!
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PingPongGame;
